import { Injectable } from '@angular/core';
import { PdfPageSelectDialogComponent } from '../components/dialogs/pdf-page-select-dialog/pdf-page-select-dialog.component';
import { MimeTypesEnum, ToastrActionEnum, ToastrStringsEnum } from '../utils/enums';
import { FileHashManager } from './autosave/file-hash-manager';
import { BlobToPdfService } from './blob-to-pdf.service';
import { ImageToPdfConversion, KesToPdfConversion, RtfToHtmlConversion } from './file-convertor/conversions';
import { FileConvertorService } from './file-convertor/file-convertor-service';
import { TabService } from './tab.service';
import { ToastrDialogService } from './toastr-dialog.service';
import { ModalWindowService } from './ui/modal-window.service';
import isTypeForPdfConversion from '../utils/helpers/pdf-conversions.helper';
import { getMimeTypeFromFile } from 'src/app/core/common';
import { normalizeMimeType, validateMimetypes } from 'src/app/services/cloud-storage/common';
import { TranslateService } from '@ngx-translate/core';
import JSZip from 'jszip';
import { EpubFileService } from './epub/epub-file.service';

export enum OpenFileAction {
  OPEN = 'OPEN',
  OCR = 'OCR',
  CLOUD = 'CLOUD',
  CLOUDOCR = 'CLOUDOCR'
}

@Injectable({
  providedIn: 'root'
})
export class OpenFileService {

  constructor(
    private tabService: TabService,
    private convertorService: FileConvertorService,
    private modalService: ModalWindowService,
    private blobToPDFService: BlobToPdfService,
    private epubFileService: EpubFileService,
    private toastrService: ToastrDialogService,
    private readonly translate: TranslateService,
  ) { }

  async open(
    file: File, 
    action: OpenFileAction, 
    metadata?: any,
    paneIndex?: number
  ): Promise<any> {

    if (!(action === OpenFileAction.CLOUD || action === OpenFileAction.CLOUDOCR)) {
      // first, make sure we have a proper File object
      // when coming from cloud, this is already done in getFile()
      // because it is a blob we receive from the corresponding cloud
      const data = await ((file as any).arrayBuffer() as Promise<ArrayBuffer>);
      const arr = new Uint8Array(data);
      const mime = getMimeTypeFromFile(file.name.toLowerCase());
      const blob = new Blob([arr], { type: file.type || mime });
      file = new File([blob], file.name, { type: normalizeMimeType(file.type || mime) });

      // if we come from cloud, validation is also already done, 
      // but we still need to validate if we came from local or exammode
      if (!this.validateFileSupport(file, action)) {
        this.toastrService.showToaster(ToastrActionEnum.Error, this.translate.instant('COMMONMESSAGEDIALOG.NOT_SUPPORTED'));
        console.error(`File not supported (1). File name: ${file.name}, type: ${file.type}`);
        return;
      }
    }

    // do we want to ocr image or pdf?
    // NOTE: only allow opening an image if it's through the ocr option
    if (action === OpenFileAction.OCR || action === OpenFileAction.CLOUDOCR) {
      this.isImageFile(file) 
        ? this.convertorService.makeConversion<ImageToPdfConversion>(ImageToPdfConversion, { file, paneIndex })
        : this.modalService.createModalWindow(PdfPageSelectDialogComponent, { loadedFile: file });
    }

    // if rtf
    else if (this.isRtfFile(file)) {
      const fileHash = (await FileHashManager.calculateFileHash(file));
      this.convertorService.makeConversion<RtfToHtmlConversion>(RtfToHtmlConversion, {
        file: file,
        fileHash,
        paneIndex,
        metadata
      });
    }

    // if kes/sek
    else if (this.isKesOrSekFile(file)) {
      this.convertorService.makeConversion<KesToPdfConversion>(KesToPdfConversion, { file, paneIndex });
    }

    // if epub
    else if (this.isEpubFile(file)) {
      await this.tabService.setTabFile(file);
    }

    // if office doc or pdf
    else if (this.isConvertibleToPdf(file)) {
      const pdfFile = await this.convertToPDFIfNeeded(file);
      const tabId = await this.tabService.setTabFile(pdfFile);

      // from asgard-cloud-base.service.ts 
      // not sure why this is needed?
      if (action === OpenFileAction.CLOUD) {
        if (tabId) {
          const activeTab = this.tabService.getTabById(tabId);
          if (activeTab) {
            activeTab.setMetadata(metadata);
          }
        } else {
          if (this.tabService.activeTab) {
            this.tabService.activeTab.setMetadata(metadata);
          }
        }
      }
    } 

    // if zip with book and annotations
    // NOTE: in zip we store epub files with saved annotations
    else if (await this.isBookFile(file)) {
      const epubWithAnnotations = await this.epubFileService.importFromZip(file);
      const tabId = await this.tabService.setTabFile(epubWithAnnotations.epubFile);
      if (tabId) {
        this.epubFileService.applyAnnotationsJson(epubWithAnnotations.annotationsJson, tabId);
      }
    }
    
    // no idea what kind of file it is
    // show error toastr
    else {
      this.toastrService.showToaster(ToastrActionEnum.Error, this.translate.instant('COMMONMESSAGEDIALOG.NOT_SUPPORTED'));
      console.error(`File not supported (2). File name: ${file.name}, type: ${file.type}`);
    }
  }

  private validateFileSupport(file: File, action: string): boolean {
    const metadata = { fileName: file.name, mimeType: file.type };
    const isOcr = action === 'OCR';
    return validateMimetypes(metadata, isOcr, true);
  }

  private isRtfFile(file: File) {
    return file.name.match(/\.rtf$/i)
    || file.type === MimeTypesEnum.TextRtf
    || (file.type === MimeTypesEnum.ApplicationMsWord && file.name.match(/\.rtf$/i))
  }

  private isKesOrSekFile(file: File): boolean {
    return !!file.name.match(/(\.kes|\.sek)$/i);
  }

  private isImageFile(file: File): boolean {
    return [
      MimeTypesEnum.ImageBmp as string, 
      MimeTypesEnum.ImageJpeg as string, 
      MimeTypesEnum.ImagePng as string
    ].includes(file.type);
  }

  private isEpubFile(file: File): boolean {
    return !!file.name.match(/\.epub$/i) || file.type === MimeTypesEnum.ApplicationEpub
  }

  private isConvertibleToPdf(file: File): boolean {
    return isTypeForPdfConversion(file.type, file.name) || file.type === MimeTypesEnum.ApplicationPdf || file.name.toLowerCase().includes('.pdf');
  }

  private async isBookFile(file: File): Promise<boolean> {
    if (!/\.alineabook$/i.test(file.name)) return false;

    try {
      const zip = await JSZip.loadAsync(file);
      const hasJson = !!zip.file('annotations.json');
      const hasEpub = zip.file(/\.epub$/i).length > 0;
      return hasJson && hasEpub;
    } catch {
      return false;
    }
  }

  private async convertToPDFIfNeeded(file: File): Promise<File> {
    let convertedFile = file;
    if (isTypeForPdfConversion(file.type, file.name)) {
      const key = 'convertingFile';
      this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.Converting, true, key);
      try {
        convertedFile = await this.blobToPDFService.convertOfficeToPDF(file);
        this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.ConversionDone, false);
      } catch (err) {
        this.toastrService.showToastrByType(ToastrActionEnum.Error, ToastrStringsEnum.ConversionFailed, false);
      }
      // close 'converting file ...' toast
      this.toastrService.hideToastr(key);
    }
    return convertedFile;
  }
}
